import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { ago } from "@/lib/format";
import { cn } from "@/lib/utils";

export function NotificationBell({ allPath = "/notifications" }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 8 } });
      setData(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const openItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    load();
    nav(n.link || allPath);
  };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative h-10 w-10 rounded-full border border-border bg-card/60 grid place-items-center hover:border-primary/40 transition-colors" data-testid="notification-bell">
          <Bell className="h-4 w-4" strokeWidth={1.7} />
          {data.unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full gold-bg text-black text-[10px] font-bold grid place-items-center" data-testid="notification-unread-count">
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0 bg-card border-border rounded-2xl overflow-hidden" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="font-display font-medium">Notifications</p>
          {data.unread > 0 && (
            <button onClick={readAll} className="text-xs text-primary flex items-center gap-1 hover:underline" data-testid="notification-mark-all-read">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {data.items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10" data-testid="notification-empty">You're all caught up.</p>
          ) : data.items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-item-${n.id}`}
              className={cn("w-full text-left px-4 py-3 border-b border-border/60 hover:bg-secondary/60 transition-colors flex gap-3", !n.read && "bg-primary/[0.04]")}>
              <span className={cn("mt-1.5 h-2 w-2 rounded-full shrink-0", n.read ? "bg-transparent" : "gold-bg")} />
              <span className="min-w-0">
                <span className="block text-sm font-medium truncate">{n.title}</span>
                <span className="block text-xs text-muted-foreground line-clamp-2">{n.message}</span>
                <span className="block text-[10px] text-muted-foreground/70 mt-1">{ago(n.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
        <button onClick={() => { setOpen(false); nav(allPath); }} className="w-full py-2.5 text-xs text-primary hover:bg-secondary/60" data-testid="notification-view-all">View all</button>
      </PopoverContent>
    </Popover>
  );
}
